// Tetrax (user request 2026-10-03): a falling-blocks game, alone or two players with garbage lines.
// Pure rules shared by the browser (which runs the player's own board) and the server (the computer
// opponent). The state is plain JSON: the duel sends it to the server and gets it back after a reload.

export const TETRAX_W = 10;
/** Visible rows. */
export const TETRAX_H = 20;
/** Two hidden rows on top, where the pieces appear. */
export const TETRAX_HIDDEN = 2;
export const TETRAX_ROWS = TETRAX_H + TETRAX_HIDDEN;
/** Cell values: 0 empty, 1–7 the pieces (I J L O S T Z), 8 a garbage block. */
export const TETRAX_GARBAGE = 8;
export const TETRAX_PIECES = 7;
export const TETRAX_NEXT_SHOWN = 5;
export const TETRAX_LOCK_MS = 500;
const LOCK_RESETS = 15;
export const TETRAX_COUNTDOWN_MS = 3000;
/** A duel lasts at most this long; then the higher score wins (as the snake game). */
export const TETRAX_DUEL_MS = 10 * 60 * 1000;
export const TETRAX_MAX_LEVEL = 20;
/** Points for 1–4 lines, × the level. */
const LINE_POINTS = [0, 100, 300, 500, 800];
/** Garbage lines sent to the opponent for 1–4 lines at once. */
const ATTACK = [0, 0, 1, 2, 4];

export interface TetraxPiece {
  type: number;
  rot: number;
  x: number;
  y: number;
}

export interface TetraxGarbage {
  id: number;
  lines: number;
  /** The column without a block. */
  hole: number;
}

export interface TetraxState {
  board: number[];
  piece: TetraxPiece | null;
  hold: number;
  holdUsed: boolean;
  queue: number[];
  /** The random generator's state (the same seed gives both duel players the same pieces). */
  rng: number;
  score: number;
  lines: number;
  level: number;
  pieces: number;
  fallMs: number;
  lockMs: number;
  lockResets: number;
  over: boolean;
  /** Garbage lines sent to the opponent in total. */
  sent: number;
  /** Garbage coming in, added under the board when a piece locks without clearing a line. */
  incoming: TetraxGarbage[];
  /** The newest garbage id received (the server's list is cumulative). */
  garbageSeen: number;
}

/** What happened in an action (sounds, the duel's updates). */
export interface TetraxEvents {
  locked: boolean;
  cleared: number;
  /** Garbage rows that rose under the board. */
  garbage: number;
  over: boolean;
}

const NONE: TetraxEvents = { locked: false, cleared: 0, garbage: 0, over: false };

// --- Pieces (the usual rotation system: spawn states and wall kicks) --------------------------------

type Cells = [number, number][];
const BASE: Record<number, { n: number; cells: Cells }> = {
  1: { n: 4, cells: [[0, 1], [1, 1], [2, 1], [3, 1]] },
  2: { n: 3, cells: [[0, 0], [0, 1], [1, 1], [2, 1]] },
  3: { n: 3, cells: [[2, 0], [0, 1], [1, 1], [2, 1]] },
  4: { n: 2, cells: [[0, 0], [1, 0], [0, 1], [1, 1]] },
  5: { n: 3, cells: [[1, 0], [2, 0], [0, 1], [1, 1]] },
  6: { n: 3, cells: [[1, 0], [0, 1], [1, 1], [2, 1]] },
  7: { n: 3, cells: [[0, 0], [1, 0], [1, 1], [2, 1]] },
};

/** SHAPES[type][rot] = cells (x, y) inside the piece's box. */
const SHAPES: Record<number, Cells[]> = {};
for (const [type, { n, cells }] of Object.entries(BASE)) {
  const rots: Cells[] = [cells];
  for (let r = 1; r < 4; r++) rots.push(rots[r - 1]!.map(([x, y]) => [n - 1 - y, x] as [number, number]));
  SHAPES[Number(type)] = Number(type) === 4 ? [cells, cells, cells, cells] : rots;
}

export function pieceCells(p: TetraxPiece): Cells {
  return SHAPES[p.type]![p.rot]!.map(([x, y]) => [p.x + x, p.y + y] as [number, number]);
}

/** Wall kicks as (dx, dy), y downwards. Key: from-state + to-state. */
const KICKS: Record<string, Cells> = {};
const KICKS_I: Record<string, Cells> = {};
const kick = (table: Record<string, Cells>, key: string, list: Cells) => (table[key] = list.map(([x, y]) => [x, -y] as [number, number]));
kick(KICKS, '01', [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]]);
kick(KICKS, '10', [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]]);
kick(KICKS, '12', [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]]);
kick(KICKS, '21', [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]]);
kick(KICKS, '23', [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]]);
kick(KICKS, '32', [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]]);
kick(KICKS, '30', [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]]);
kick(KICKS, '03', [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]]);
kick(KICKS_I, '01', [[0, 0], [-2, 0], [1, 0], [-2, -1], [1, 2]]);
kick(KICKS_I, '10', [[0, 0], [2, 0], [-1, 0], [2, 1], [-1, -2]]);
kick(KICKS_I, '12', [[0, 0], [-1, 0], [2, 0], [-1, 2], [2, -1]]);
kick(KICKS_I, '21', [[0, 0], [1, 0], [-2, 0], [1, -2], [-2, 1]]);
kick(KICKS_I, '23', [[0, 0], [2, 0], [-1, 0], [2, 1], [-1, -2]]);
kick(KICKS_I, '32', [[0, 0], [-2, 0], [1, 0], [-2, -1], [1, 2]]);
kick(KICKS_I, '30', [[0, 0], [1, 0], [-2, 0], [1, -2], [-2, 1]]);
kick(KICKS_I, '03', [[0, 0], [-1, 0], [2, 0], [-1, 2], [2, -1]]);

// --- Random pieces --------------------------------------------------------------------------------

/** A small seeded generator (mulberry32): returns [0, 1) and the next state. */
function nextRandom(s: TetraxState): number {
  s.rng = (s.rng + 0x6d2b79f5) | 0;
  let t = s.rng;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** The pieces come in shuffled sets of all seven, so none is missing for long. */
function fillQueue(s: TetraxState): void {
  while (s.queue.length <= TETRAX_NEXT_SHOWN) {
    const bag = [1, 2, 3, 4, 5, 6, 7];
    for (let i = bag.length - 1; i > 0; i--) {
      const j = Math.floor(nextRandom(s) * (i + 1));
      [bag[i], bag[j]] = [bag[j]!, bag[i]!];
    }
    s.queue.push(...bag);
  }
}

// --- The game -------------------------------------------------------------------------------------

export function newGame(seed: number): TetraxState {
  const s: TetraxState = {
    board: new Array<number>(TETRAX_W * TETRAX_ROWS).fill(0),
    piece: null,
    hold: 0,
    holdUsed: false,
    queue: [],
    rng: seed | 0,
    score: 0,
    lines: 0,
    level: 1,
    pieces: 0,
    fallMs: 0,
    lockMs: 0,
    lockResets: 0,
    over: false,
    sent: 0,
    incoming: [],
    garbageSeen: 0,
  };
  fillQueue(s);
  spawn(s, s.queue.shift()!);
  return s;
}

/** Milliseconds per row at a level (the usual curve: 1 s at level 1, fast from level 10 on). */
export function gravityMs(level: number): number {
  const l = Math.min(level, TETRAX_MAX_LEVEL);
  return Math.max(16, Math.pow(0.8 - (l - 1) * 0.007, l - 1) * 1000);
}

export function fits(s: TetraxState, p: TetraxPiece): boolean {
  for (const [x, y] of pieceCells(p)) {
    if (x < 0 || x >= TETRAX_W || y >= TETRAX_ROWS) return false;
    if (y >= 0 && s.board[y * TETRAX_W + x] !== 0) return false;
  }
  return true;
}

function spawn(s: TetraxState, type: number): void {
  fillQueue(s);
  const p: TetraxPiece = { type, rot: 0, x: type === 4 ? 4 : 3, y: 0 };
  s.fallMs = 0;
  s.lockMs = 0;
  s.lockResets = 0;
  if (!fits(s, p)) {
    s.piece = null;
    s.over = true;
    return;
  }
  s.piece = p;
  // The piece drops into view at once when there is room.
  const down = { ...p, y: p.y + 1 };
  if (fits(s, down)) s.piece = down;
}

const grounded = (s: TetraxState) => s.piece !== null && !fits(s, { ...s.piece, y: s.piece.y + 1 });

/** A move or a turn on the ground gives more time before the piece locks (a limited number of times). */
function moved(s: TetraxState): void {
  if (grounded(s) && s.lockResets < LOCK_RESETS) {
    s.lockResets++;
    s.lockMs = 0;
  }
}

export function move(s: TetraxState, dx: number): boolean {
  if (s.over || !s.piece) return false;
  const p = { ...s.piece, x: s.piece.x + dx };
  if (!fits(s, p)) return false;
  s.piece = p;
  moved(s);
  return true;
}

/** Turn clockwise (1) or counter-clockwise (-1), with the wall kicks. */
export function rotate(s: TetraxState, dir: 1 | -1): boolean {
  if (s.over || !s.piece || s.piece.type === 4) return false;
  const from = s.piece.rot;
  const to = (from + dir + 4) % 4;
  const kicks = (s.piece.type === 1 ? KICKS_I : KICKS)[`${from}${to}`]!;
  for (const [dx, dy] of kicks) {
    const p = { ...s.piece, rot: to, x: s.piece.x + dx, y: s.piece.y + dy };
    if (fits(s, p)) {
      s.piece = p;
      moved(s);
      return true;
    }
  }
  return false;
}

/** One row down by the player: a point per row. */
export function softDrop(s: TetraxState): boolean {
  if (s.over || !s.piece) return false;
  const p = { ...s.piece, y: s.piece.y + 1 };
  if (!fits(s, p)) return false;
  s.piece = p;
  s.fallMs = 0;
  s.score += 1;
  return true;
}

/** The row where the piece would land (the ghost piece). */
export function dropY(s: TetraxState, piece: TetraxPiece | null = s.piece): number {
  if (!piece) return 0;
  let y = piece.y;
  while (fits(s, { ...piece, y: y + 1 })) y++;
  return y;
}

/** Straight down and locked: two points per row. */
export function hardDrop(s: TetraxState): TetraxEvents {
  if (s.over || !s.piece) return NONE;
  const y = dropY(s);
  s.score += 2 * (y - s.piece.y);
  s.piece = { ...s.piece, y };
  return lock(s);
}

/** Puts the piece aside (once per piece) and takes the one held before, or the next. */
export function hold(s: TetraxState): boolean {
  if (s.over || !s.piece || s.holdUsed) return false;
  const current = s.piece.type;
  const next = s.hold || s.queue.shift()!;
  s.hold = current;
  spawn(s, next);
  s.holdUsed = true;
  return true;
}

/** Time passes: the piece falls, and locks after a moment on the ground. */
export function tick(s: TetraxState, ms: number): TetraxEvents {
  if (s.over || !s.piece) return NONE;
  if (grounded(s)) {
    s.fallMs = 0;
    s.lockMs += ms;
    return s.lockMs >= TETRAX_LOCK_MS ? lock(s) : NONE;
  }
  s.lockMs = 0;
  s.fallMs += ms;
  const g = gravityMs(s.level);
  while (s.fallMs >= g) {
    s.fallMs -= g;
    if (!fits(s, { ...s.piece, y: s.piece.y + 1 })) {
      s.fallMs = 0;
      break;
    }
    s.piece = { ...s.piece, y: s.piece.y + 1 };
  }
  return NONE;
}

function lock(s: TetraxState): TetraxEvents {
  const p = s.piece!;
  const cells = pieceCells(p);
  for (const [x, y] of cells) if (y >= 0) s.board[y * TETRAX_W + x] = p.type;
  s.piece = null;
  s.pieces++;
  // Locked wholly above the visible board: the game is over.
  if (cells.every(([, y]) => y < TETRAX_HIDDEN)) {
    s.over = true;
    return { locked: true, cleared: 0, garbage: 0, over: true };
  }
  const cleared = clearLines(s);
  let garbage = 0;
  if (cleared > 0) {
    s.score += LINE_POINTS[cleared]! * s.level;
    s.lines += cleared;
    s.level = Math.min(TETRAX_MAX_LEVEL, 1 + Math.floor(s.lines / 10));
    // The lines first cancel garbage coming in; the rest goes to the opponent.
    let attack = ATTACK[cleared]!;
    while (attack > 0 && s.incoming.length > 0) {
      const g = s.incoming[0]!;
      const used = Math.min(attack, g.lines);
      g.lines -= used;
      attack -= used;
      if (g.lines === 0) s.incoming.shift();
    }
    s.sent += attack;
  } else {
    garbage = riseGarbage(s);
  }
  if (!s.over) {
    spawn(s, s.queue.shift()!);
    s.holdUsed = false;
  }
  return { locked: true, cleared, garbage, over: s.over };
}

function clearLines(s: TetraxState): number {
  const kept: number[][] = [];
  let cleared = 0;
  for (let y = 0; y < TETRAX_ROWS; y++) {
    const row = s.board.slice(y * TETRAX_W, (y + 1) * TETRAX_W);
    if (row.every((c) => c !== 0)) cleared++;
    else kept.push(row);
  }
  if (cleared === 0) return 0;
  s.board = [...new Array<number>(cleared * TETRAX_W).fill(0), ...kept.flat()];
  return cleared;
}

/** The garbage coming in rises under the board. A block pushed over the top ends the game. */
function riseGarbage(s: TetraxState): number {
  let total = 0;
  for (const g of s.incoming) {
    for (let i = 0; i < g.lines; i++) {
      if (s.board.slice(0, TETRAX_W).some((c) => c !== 0)) s.over = true;
      const row = new Array<number>(TETRAX_W).fill(TETRAX_GARBAGE);
      row[g.hole] = 0;
      s.board = [...s.board.slice(TETRAX_W), ...row];
      total++;
    }
  }
  s.incoming = [];
  return total;
}

/** Garbage from the server: the entries not seen yet. */
export function receiveGarbage(s: TetraxState, list: readonly TetraxGarbage[]): number {
  let added = 0;
  for (const g of list) {
    if (g.id <= s.garbageSeen) continue;
    s.garbageSeen = g.id;
    s.incoming.push({ ...g });
    added += g.lines;
  }
  return added;
}

export const pendingGarbage = (s: TetraxState) => s.incoming.reduce((n, g) => n + g.lines, 0);

/** A state sent by a browser: the right shape and values (it is only shown and kept, not trusted further). */
export function validState(v: unknown): v is TetraxState {
  if (!v || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  const int = (x: unknown, max: number) => typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= max;
  const board = s.board;
  if (!Array.isArray(board) || board.length !== TETRAX_W * TETRAX_ROWS || !board.every((c) => int(c, TETRAX_GARBAGE))) return false;
  const p = s.piece as Record<string, unknown> | null;
  if (p !== null && (typeof p !== 'object' || !int(p.type, 7) || p.type === 0 || !int(p.rot, 3) || typeof p.x !== 'number' || typeof p.y !== 'number' || Math.abs(p.x) > 20 || Math.abs(p.y) > 30)) return false;
  if (!Array.isArray(s.queue) || s.queue.length > 20 || !s.queue.every((q) => int(q, 7))) return false;
  if (!Array.isArray(s.incoming) || s.incoming.length > 100 || !s.incoming.every((g) => g && int(g.id, 1e9) && int(g.lines, 100) && int(g.hole, TETRAX_W - 1))) return false;
  return (
    int(s.hold, 7) &&
    typeof s.holdUsed === 'boolean' &&
    typeof s.rng === 'number' &&
    int(s.score, 1e9) &&
    int(s.lines, 1e6) &&
    int(s.level, TETRAX_MAX_LEVEL) &&
    int(s.pieces, 1e6) &&
    typeof s.over === 'boolean' &&
    int(s.sent, 1e6) &&
    int(s.garbageSeen, 1e9) &&
    typeof s.fallMs === 'number' &&
    typeof s.lockMs === 'number' &&
    int(s.lockResets, 100)
  );
}
